import {
	ACCESS_RESOURCES,
	type AccessAction,
	type FeatureKey,
	type ResourceAccessConfig,
	type ResourceId,
	pickPermissionKey,
} from '@/config/access-control'
import type { QuotaType } from '@/config/plans'
import { isOrganizationLevelRoute } from '@/lib/routerUtils'
import type { Permissions } from '@/rbac/grants'
import { useAuthStore } from '@/stores/authStore'
import type { EntityDescriptor } from '@/utils/access-control/perEntity'
import { getConsoleUrl, isConsoleDeployment } from '@/utils/deployment'
import { useIsOwner } from '@/utils/permissions'
import { useLocation } from '@tanstack/react-router'
import { useMemo } from 'react'
import type { AccessState } from './types'
import { useEntityAccess } from './useEntityAccess'
import { useEntitySetAccess } from './useEntitySetAccess'
import { useFeatureAccess } from './useFeatureAccess'
import { useQuota } from './useQuota'
import { useRbac } from './useRbac'

export interface AccessControlInput {
	/** Pick a pre-configured resource from the registry. */
	resource?: ResourceId
	/**
	 * The action being attempted against the resource. Determines which
	 * permission slot (view/create/edit/delete/bulk) on the resource config is read.
	 *
	 * Defaults to 'create' for backwards compatibility with Phase 1 call sites
	 * that wrap "Create X" buttons.
	 */
	action?: AccessAction
	/** Override or specify RBAC permission directly. */
	permission?: keyof Permissions
	/** Override or specify plan-feature directly. */
	featureKey?: FeatureKey
	/** Override or specify quota directly. */
	quotaKey?: QuotaType
	/**
	 * Per-entity descriptor (see `EntityDescriptor` in `@/utils/access-control/perEntity`:
	 * device, stream, action_type / action_type_id, owners, metadata, metadata_key,
	 * firmware, files, dbc_parser, session_rule, exporter, subscriber_group, config).
	 * When set, this per-entity check is mandatory: it must pass for access to be granted.
	 * For dashboards this is the `owners` check, which gates edit/delete by ownership.
	 */
	entity?: EntityDescriptor
	/**
	 * Per-entity-set descriptor for bulk operations. When supplied, the bulk
	 * action is allowed only when *every* entity in the set passes its individual
	 * check (default "all-allowed" semantics). Empty sets pass.
	 *
	 * Use `entity` for single-row mutations and `entities` for multi-row bulk
	 * mutations. Supplying both is supported but unusual — both checks must pass.
	 */
	entities?: EntityDescriptor[]
	/** Custom warning threshold (0..1). Default 0.8. */
	warningThreshold?: number
}

/**
 * Composes RBAC + plan-feature + quota into a single discriminated AccessState.
 *
 * Precedence:
 *   0. org owner on org-level route    — bypass RBAC, entity checks, and plan-lock only (quota still applies).
 *   1. loading (quota fetch in flight) — optimistic, let UI render children.
 *   2. denied (no-permission)          — RBAC fails fast; plan/quota irrelevant.
 *   3. blocked (plan-locked)           — user is entitled by RBAC but not by tier.
 *   4. blocked (quota-exceeded)        — over capacity.
 *   5. warning (quota-warning)         — approaching threshold; action allowed.
 *   6. allowed
 */
export function useAccessControl(input: AccessControlInput): AccessState {
	const resolvedConfig: ResourceAccessConfig | undefined = input.resource ? ACCESS_RESOURCES[input.resource] : undefined
	const resolved: AccessControlInput & Partial<ResourceAccessConfig> = resolvedConfig
		? { ...resolvedConfig, ...input }
		: input
	const action: AccessAction = input.action ?? 'create'

	const effectivePermission = resolved.permission ?? pickPermissionKey(resolvedConfig, action)
	const effectiveFeatureKey = resolved.featureKey
	const effectiveQuotaKey = resolved.quotaKey

	const rbac = useRbac(effectivePermission, action)
	const entity = useEntityAccess(resolved.entity)
	const entitySet = useEntitySetAccess(resolved.entities)
	const { pathname } = useLocation()
	const ownerAtOrgLevel = useIsOwner() && isOrganizationLevelRoute(pathname)
	const feature = useFeatureAccess(effectiveFeatureKey)
	const quotaApplies = action === 'create'
	const quota = useQuota(quotaApplies ? effectiveQuotaKey : undefined, { threshold: resolved.warningThreshold })
	const hasQuota = quota !== null

	const orgId = useAuthStore(s => s.currentOrganizationId) ?? ''
	const billingHref = useMemo(() => {
		const baseHref = (resolved.billingHref ?? ((id: string) => `/orgs/${id}/billing/plan-details`))(orgId)
		if (isConsoleDeployment()) {
			return baseHref
		}
		return `${getConsoleUrl()}${baseHref}`
	}, [resolved.billingHref, orgId])

	return useMemo<AccessState>(() => {
		// Only block on quota while the fetch is actually in flight. When the fetch has
		// settled but the slice is still absent (errored/pending), fall through optimistically
		// instead of sticking in 'loading' forever — RBAC has already passed at this point.
		// The exceeded/warning branches below are guarded against null used/total.
		if (hasQuota && quota.isLoading) {
			return { state: 'loading' }
		}

		if (!ownerAtOrgLevel) {
			if (!rbac.allowed) {
				return { state: 'denied', reason: 'no-permission' }
			}
			if (!entity.allowed && resolved.entity) {
				return { state: 'denied', reason: 'no-permission' }
			}
			if (!entitySet.allowed) {
				return { state: 'denied', reason: 'no-permission' }
			}
			if (!feature.allowed && effectiveFeatureKey) {
				return {
					state: 'blocked',
					reason: 'plan-locked',
					featureKey: effectiveFeatureKey,
					requiredPlan: feature.requiredPlan,
					currentPlan: feature.currentPlan,
					billingHref,
				}
			}
		}

		if (hasQuota && effectiveQuotaKey && quota.status === 'exceeded' && quota.used != null && quota.total != null) {
			return {
				state: 'blocked',
				reason: 'quota-exceeded',
				quotaKey: effectiveQuotaKey,
				usage: { used: quota.used, total: quota.total },
				billingHref,
			}
		}
		if (hasQuota && effectiveQuotaKey && quota.status === 'warning' && quota.used != null && quota.total != null) {
			return {
				state: 'warning',
				reason: 'quota-warning',
				quotaKey: effectiveQuotaKey,
				usage: { used: quota.used, total: quota.total },
				billingHref,
			}
		}
		return { state: 'allowed' }
	}, [
		billingHref,
		effectiveFeatureKey,
		effectiveQuotaKey,
		entity.allowed,
		entitySet.allowed,
		feature.allowed,
		feature.currentPlan,
		feature.requiredPlan,
		hasQuota,
		ownerAtOrgLevel,
		quota?.isLoading,
		quota?.status,
		quota?.total,
		quota?.used,
		rbac.allowed,
		resolved.entity,
	])
}
